// import { Search } from "@mui/icons-material";
import { Box, Breadcrumbs, /* Button, */ Divider, Grid, /* InputAdornment, */ Link, /* TextField, */ Typography } from "@mui/material";
import React from "react";
import { Helmet } from "react-helmet-async";
import { useDispatch, useSelector } from "react-redux";

import AppLoader from "../components/AppLoader";
import ContentWrapper from "../components/ContentWrapper";
// import FilterListTooltip from "../components/FilterListTooltip";
import FilterNav from "../components/FilterNav";
import PageHeading from "../components/PageHeading";
import TableAdvanced from "../components/TableAdvanced";
import { operatorsCells } from "../constants";
import allRoutes from "../constants/routes";
import { useSaveSearchMutation } from "../store/apis/save";
import { useGetCountriesQuery, useGetOperatorsQuery, useLazyExportOperatorsQuery } from "../store/apis/search";
// import { selectSaveToken } from "../store/slices/auth";
import { selectOperatorsSlice, /* setFilter, */ setOrder, setPage, setRowsPerPage, setSavedValues } from "../store/slices/operators";
import { selectUiState, setDense, setFontSize } from "../store/slices/ui";
import { OperatorType } from "../types";
import { mapValuesToSaveData } from "../utils";
import ExportCsv from "../components/ExportCsv";
import { isEqual } from "lodash";
import SaveSearch from "../components/SaveSearch";
import { selectExport } from "../store/slices/auth";

const OperatorsSearch: React.FC = () => {
  const dispatch = useDispatch();
  const { dense, fontSize, exportFields } = useSelector(selectUiState);
  const { sortBy, desc, rowsPerPage, page, filter, values, lastSaved } = useSelector(selectOperatorsSlice);
  const exportEnabled = useSelector(selectExport);
  // const token = useSelector(selectSaveToken);
  const [saveSearch, { isLoading: isSaving }] = useSaveSearchMutation();
  const { data: countries = [] } = useGetCountriesQuery({
    ituRegions: [],
    regions: values.regions,
  });

  const [trigger, { data: download, isLoading: isExporting, isFetching: isExportingFetching }] = useLazyExportOperatorsQuery();
  const {
    data = { results: [], total: 0, count: 0 },
    isLoading,
    isFetching,
    isUninitialized,
  } = useGetOperatorsQuery({
    rowsPerPage,
    page,
    values,
    sortBy,
    sortDirection: desc ? "desc" : "asc",
    format: "json",
  });

  const { results = [] } = data;
  const loading = isLoading || isFetching || isUninitialized;
  const total = data?.count || 0;
  const pages = Math.ceil(total / rowsPerPage);
  const trimmedResults = results.map((item: OperatorType) => {
    const foundCountryObject = countries.find(countryItem => countryItem.id === item.country_id);
    return { ...item, country_id: foundCountryObject?.title || "-" };
  });
  const filteredResults = trimmedResults.filter((item: OperatorType) => {
    if (filter.length > 0) {
      return item.title.toLowerCase().indexOf(filter.toLowerCase()) > -1;
    } else return true;
  });

  const handleSave = (title: string) => {
    dispatch(setSavedValues(values));
    saveSearch({ sectionId: 4, data: { title, ...mapValuesToSaveData(4, values) } });
  };

  const renderButtons = () => (
    <Box paddingY={4}>
      <Grid container spacing={4} alignItems="center">
        <Grid>
          <SaveSearch disabled={isEqual(values, lastSaved)} loading={isSaving} onClick={handleSave} />
        </Grid>
        <Grid>
          <ExportCsv
            disabled={exportEnabled ? false : total > 2000}
            source={download}
            loading={isExporting || isExportingFetching}
            onClick={format => trigger({ values: values, fields: exportFields, format })}
          />
        </Grid>
      </Grid>
    </Box>
  );

  return (
    <>
      <Helmet title="Operators - GSA GAMBoD" />
      <FilterNav />
      <PageHeading
        title={allRoutes.operators.label}
        icon={allRoutes.operators.icon}
        color={allRoutes.operators.color}
        section="operators"
        inset
      >
        <Breadcrumbs aria-label="Breadcrumb">
          <Link href={allRoutes.dashboard.path}>Dashboard</Link>
          <Link href={allRoutes.operators.path}>Operators</Link>
          <Typography>Search</Typography>
        </Breadcrumbs>
      </PageHeading>
      <ContentWrapper inset>
        <Grid container justifyContent="space-between" alignItems="center">
          <Grid size="grow">
            <Box paddingY={10}>
              <Grid container spacing={8} alignItems="flex-end">
                <Grid sx={{ display: { xs: "none", md: "block" } }}>
                  <Typography variant="h4">Your Results</Typography>
                </Grid>
                <Grid>
                  <Typography variant="subtitle2" gutterBottom>
                    Returned results
                  </Typography>
                  <Typography variant="h4">
                    <span data-testid="operatorsTotal" data-testvalue={total}>
                      {total.toLocaleString()}
                    </span>{" "}
                    Operators
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          </Grid>
          <Grid sx={{ display: { xs: "none", md: "block" } }}>{renderButtons()}</Grid>
        </Grid>
      </ContentWrapper>
      <Box sx={{ display: { xs: "block", md: "none" } }}>
        <ContentWrapper inset>
          <Divider />
          {renderButtons()}
        </ContentWrapper>
      </Box>
      <Divider />
      <AppLoader loading={loading} />
      <ContentWrapper body inset>
        {/* <Box paddingY={4}>
          <Grid container alignItems="center" wrap="nowrap">
            <Grid >
              <FilterListTooltip title="Search operators by name" />
            </Grid>
            <Grid >
              <Grid container alignItems="center" spacing={2} wrap="nowrap">
                <Grid >
                  Filter the results by name: &nbsp;
                </Grid>
                <Grid >
                  <TextField
                    id="input-with-icon-textfield"
                    variant="outlined"
                    size="small"
                    value={filter}
                    placeholder="Search results"
                    onChange={event => {
                      dispatch(setFilter(event.target.value));
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid >
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      dispatch(setFilter(""));
                    }}
                  >
                    Clear
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>
        <Divider /> */}
        <TableAdvanced
          title={`GAMBoD Operators Search - ${total.toLocaleString()} results, page ${page}`}
          dense={dense}
          fontSize={fontSize}
          rows={filteredResults}
          cellsMap={operatorsCells(sortBy, desc)}
          page={page}
          pages={pages}
          rowsPerPage={rowsPerPage}
          loading={loading}
          setRowsPerPage={value => {
            dispatch(setRowsPerPage(value));
          }}
          setPage={value => {
            dispatch(setPage(value));
          }}
          setFontSize={value => {
            dispatch(setFontSize(value));
          }}
          setDense={value => {
            dispatch(setDense(value));
          }}
          onOrder={value => {
            dispatch(setOrder(value));
          }}
        />
      </ContentWrapper>
    </>
  );
};

export default OperatorsSearch;
